<template>
	<v-row>
		<v-col>

			<v-form v-if="this.partner" v-model="formIsValid.place" ref="placeForm" new-forms>
				<ui-box :form="$refs.placeForm" new-forms>

					<form-title title="Place"></form-title>

					<form-divider inner/>

					<v-row>
						<v-col>
							<venue-select
								v-model="coupon.venue_id"
								:rules="rules.place"
								:venues="venues"
							></venue-select>
						</v-col>
					</v-row>
				</ui-box>
			</v-form>

			<form-divider/>

			<v-form v-model="formIsValid.couponType" ref="couponTypeForm" new-forms>

				<ui-box
						v-if="shouldShowSection('child_template')"
						:form="$refs.couponTypeForm"
						:eager="!!product.id"
						new-forms
				>

					<form-title title="Coupon Type">
						There are several different types of coupons, choose wisely!
					</form-title>

					<form-divider inner/>

					<!-- Child Coupon Template -->
					<v-row>
						<v-col>
							<v-select
									v-model="selectedCouponTemplate"
									class="rounded-lg required"
									clearable
									:items="templates"
									item-text="name"
									label="Coupon Type"
									outlined
									return-object
									:rules="rules.template_id"
							></v-select>
						</v-col>
					</v-row>
					<!-- End: Child Coupon Template -->

				</ui-box>

			</v-form>

			<form-divider/>

			<v-form
					v-model="formIsValid.savings"
					ref="savingsForm"
					new-forms
			>

				<ui-box
						v-if="shouldShowSection('savings')"
						:form="$refs.savingsForm"
						:eager="!!product.id"
						new-forms
				>

					<form-title title="Savings Info">
						Offer a great value to your customers.
					</form-title>

					<form-divider inner/>

					<!-- Savings Info -->
					<template v-if="selectedCouponTemplate?.parent_id !== 0">
						<span class="text-caption">Coupon Name</span>
						<h4 class="font-weight-light mb-8 mt-4 secondary--text">
							{{ couponName }}
						</h4>
						<v-alert
								v-if="!validProductName"
								class="my-8 rounded-lg"
								border="left"
								color="warning"
								dense
								icon="mdi-alert-circle"
								text
						>
							<span :class="{ 'text-body-2': $vuetify.breakpoint.smAndDown }">
								<span class="font-weight-bold">Warning: </span>
								The current coupon name <strong>{{ coupon.name }}</strong> is not valid.
								You may update it below to fit your needs.
							</span>
						</v-alert>
					</template>
					<template v-else>
						<v-row>
							<v-col>
								<v-text-field
										v-model="coupon.name"
										class="rounded-lg required"
										clearable
										label="Coupon Name"
										outlined
										:rules="rules.name"
								></v-text-field>
							</v-col>
						</v-row>
					</template>

					<!-- Basic -->
					<v-row v-if="isBasicCoupon">
						<v-col cols="12">
							<v-textarea
									v-model="coupon.settings.coupon_text"
									v-if="isCouponType('bw_basic_coupon')"
									key="bw_text"
									auto-grow
									class="rounded-lg required"
									hint="Be sure that this coupon cannot be categorized by one of the other coupon types."
									label="Coupon Text"
									outlined
									persistent-hint
									rows="1"
									:rules="rules.basic"
									@keydown.enter.prevent
							></v-textarea>
						</v-col>
					</v-row>
					<!-- End: Basic -->

					<!-- $/% Off Item -->
					<v-row v-if="isDiscountCoupon">
						<v-col cols="12" sm="5" md="4" lg="3">
							<v-text-field
									v-model.number="coupon.settings.coupon_percent_off"
									v-if="isCouponType('bw_percent_off')"
									key="bw_percent_off"
									class="rounded-lg required"
									hide-spin-buttons
									hint="Minimum of 10%"
									label="Savings"
									max="100"
									min="10"
									outlined
									persistent-hint
									:rules="isCouponType('bw_percent_off') && rules.percent_off"
									suffix=" % Off"
									type="number"
									value="20"
									@wheel.native.prevent
							></v-text-field>
							<v-text-field
									v-model.number="coupon.settings.coupon_amount_off"
									v-if="isCouponType('bw_dollar_off')"
									v-two-decimals
									key="bw_dollar_off"
									class="rounded-lg required"
									hide-spin-buttons
									hint="Minimum of $1.00"
									label="Savings"
									min="1"
									outlined
									persistent-hint
									prefix="$ "
									:rules="isCouponType('bw_dollar_off') && rules.amount_off"
									suffix="Off"
									type="number"
									@wheel.native.prevent
							></v-text-field>
						</v-col>
						<v-col cols="12" sm="7" md="8" lg="9">
							<v-text-field
									v-model="coupon.settings.coupon_discount_item"
									v-if="isDiscountCoupon"
									class="rounded-lg"
									hint='Ex: Large 16" Pizza, Oil Change, Your Entire Purchase'
									label="Discount Name"
									outlined
									persistent-hint
									type="text"
							></v-text-field>
						</v-col>
					</v-row>
					<!-- End: $/% Off Item -->

					<!-- Buy One Get One Free/% -->
					<v-row v-if="isBogoCoupon">
						<v-col>
							<v-text-field
									v-model="coupon.settings.coupon_bogo_item"
									class="rounded-lg required"
									label="BOGO Item Name"
									outlined
									:rules="isBogoCoupon && rules.bogo_item"
							></v-text-field>
						</v-col>
					</v-row>
					<!-- End: Buy One Get One Free/% -->

					<!-- Free ___ with purchase of ___ -->
					<v-row v-if="isCouponType('bw_free_with_purchase_of')">
						<v-col>
							<v-text-field
									v-model="coupon.settings.coupon_free_item"
									class="rounded-lg required"
									label="Free Service/Item"
									outlined
									:prefix="couponPropExists(coupon, 'settings.coupon_free_item') ? 'Free' : undefined"
									:rules="isCouponType('bw_free_with_purchase_of') && rules.free_item"
							></v-text-field>
						</v-col>
						<v-col>
							<v-text-field
									v-model="coupon.settings.coupon_purchase_item"
									class="rounded-lg required"
									label="Purchased Service/Item"
									outlined
									:prefix="couponPropExists(coupon, 'settings.coupon_purchase_item') ? 'With Purchase of' : undefined"
									:rules="isCouponType('bw_free_with_purchase_of') && rules.purchase_item"
							></v-text-field>
						</v-col>
					</v-row>
					<!-- End: Free ___ With Purchase Of ___ -->

					<!-- Free ___ -->
					<v-row v-if="isCouponType('bw_free_coupon')">
						<v-col>
							<v-text-field
									v-model="coupon.settings.coupon_free_service_item"
									class="rounded-lg required"
									label="Free Service/Item"
									outlined
									:prefix="couponPropExists(coupon, 'settings.coupon_free_service_item') ? 'Free' : undefined"
									:rules="isCouponType('bw_free_coupon') && rules.free_item"
							></v-text-field>
						</v-col>
					</v-row>
					<!-- End: Free ___ -->

					<!-- Coupon Wizard -->
					<div
							v-if="selectedCouponTemplate?.id && selectedCouponTemplate?.parent_id !== 0 && !isBasicCoupon"
							class="mt-8">
						<v-btn
								v-if="!showCouponWizard.savingsInfo"
								class="mb-6 ms-2 mt-2 rounded-lg"
								color="info"
								depressed
								@click="showCouponWizard.savingsInfo = !showCouponWizard.savingsInfo"
						>
							<v-icon class="wiz-btn-icon" large>
								mdi-auto-fix
							</v-icon>
							Coupon Wizard
						</v-btn>
						<v-alert
								v-model="showCouponWizard.savingsInfo"
								v-if="selectedCouponTemplate"
								dismissible
								prominent
								rounded="lg"
								text
								type="info"
						>
							<template v-slot:prepend>
								<v-icon
										class="align-self-start me-8 ms-2 mt-2 v-alert__icon"
										color="info"
										dark
								>
									mdi-auto-fix
								</v-icon>
							</template>
							<v-row>
								<v-col cols="12" md="5">
									<h5 class="mt-0">Coupon Wizard</h5>
									<div class="text-body-2">
										Common examples of this coupon type look like:
									</div>
								</v-col>
								<v-col cols="12" md="7">
									<ul v-if="isBogoCoupon" class="mt-1 text-body-2">
										<li>Buy One Pizza, Get One Pizza 50% Off</li>
										<li>Buy One Drink, Get One Drink Free</li>
									</ul>
									<ul v-if="isDiscountCoupon" class="mt-1 text-body-2">
										<li>$10 Off Large Two (2) Topping Pizza</li>
										<li>20% Off Your Entire Order</li>
										<li>$15 Off Any One (1) Item</li>
									</ul>
									<ul v-if="isCouponType('bw_free_with_purchase_of')"
											class="mt-1 text-body-2">
										<li>Free Dessert With Purchase Of Entree</li>
										<li>Free Car Wash With Purchase Of Oil Change</li>
										<li>Free Corkscrew With Purchase OF Any Wine</li>
									</ul>
									<ul v-if="isCouponType('bw_free_coupon')"
											class="mt-1 text-body-2">
										<li>Free Admission</li>
										<li>Free Appetizer</li>
										<li>Free Sticker</li>
									</ul>
								</v-col>
							</v-row>
							<v-divider class="my-2 opacity-50" light></v-divider>
							<v-row>
								<v-col>
									<div class="text-caption">
										Make sure to look at the generated coupon name above to ensure its
										accuracy.
									</div>
								</v-col>
							</v-row>
						</v-alert>
					</div>
					<!-- End: Coupon Wizard -->
					<!-- End: Savings Info -->
				</ui-box>

			</v-form>

			<form-divider/>

			<v-form v-model="formIsValid.details" ref="detailsForm" new-forms>

				<ui-box
						v-if="shouldShowSection('description')"
						:form="$refs.detailsForm"
						:eager="!!product.id"
						new-forms
				>

					<form-title title="Description & Restrictions">
						Any extra info about coupon usage and restrictions.
					</form-title>

					<form-divider inner/>

					<!-- Description / Restrictions -->
					<v-row>
						<v-col>
							<wysiwyg-input
									v-model="coupon.short_information"
									:counter="1250"
									hide-details="auto"
									label="Brief Description"
									outlined
									rows="4"
							/>
						</v-col>
					</v-row>
					<v-row>
						<v-col>
							<wysiwyg-input
									v-model="coupon.restrictions"
									:counter="1250"
									hide-details="auto"
									hint="If there are no restrictions on use for this offer, leave blank."
									label="Coupon Restrictions"
									outlined
									persistent-hint
									rows="4"
							/>
						</v-col>
					</v-row>
					<!-- End: Description / Restrictions -->

				</ui-box>

			</v-form>

			<form-divider/>

			<v-form v-model="formIsValid.blackoutDates" ref="blackoutDatesForm" class="mb-8">
				<blackout-dates
						:refs="$refs"
						:product="product"
						:coupon="coupon"
						:form-is-valid="formIsValid"
						:timezone="partner?.timezone || timezone"
				/>
			</v-form>

			<v-form v-model="formIsValid.repeat" ref="repeatForm" new-forms>

				<ui-box
						v-if="shouldShowSection('repeat')"
						:form="$refs.repeatForm"
						:eager="!!product.id"
						new-forms
				>

					<form-title title="Repeat Usage">
						Allows you to set customer and overall usage of your coupon.
					</form-title>

					<form-divider inner/>

					<v-row>
						<v-col cols="12" md="5" lg="4">
							<v-switch
									v-model="coupon.repeat"
									:class="coupon.repeat ? 'mt-3' : 'mb-2 mt-0'"
									dense
									hide-details="auto"
									inset
									label="Is this a repeat use coupon?"
							></v-switch>
						</v-col>
						<v-col cols="12" md="7" lg="8">
							<v-row v-if="coupon.repeat">
								<v-col>
									<v-text-field
											v-model.number="coupon.repeat_delay"
											class="flex-grow-1 rounded-lg required"
											clearable
											hide-spin-buttons
											label="Repeat Delay"
											min="1"
											outlined
											prefix="Every"
											:rules="rules.repeat_delay"
											type="number"
											@wheel.native.prevent
									></v-text-field>
								</v-col>
								<v-col>
									<v-select
											v-model="coupon.repeat_delay_type"
											class="flex-grow-1 rounded-lg required"
											clearable
											:items="['hours', 'days', 'weeks', 'months']"
											label="Repeat Delay Type"
											outlined
											:rules="rules.repeat_delay_type"
									></v-select>
								</v-col>
							</v-row>
						</v-col>
					</v-row>
				</ui-box>

			</v-form>

			<form-divider/>

			<v-form v-model="formIsValid.redeemForm" ref="redeemForm" class="mb-8">
				<redeem-form
						:coupon="coupon"
						:form-is-valid="formIsValid"
						:origin="origin"
						:product="product"
						:refs="$refs"
						:rules="rules"
						:show-redemption-validations="origin !== 'merchant-portal'"
						:venue="localVenue"
				/>
			</v-form>

			<form-divider v-if="origin=='merchant-portal'"/>

			<v-form v-if="origin=='merchant-portal'"
							v-model="formIsValid.availability" ref="availabilityForm" class="mb-8"
							new-forms>

				<ui-box :form="$refs.availabilityForm" :eager="!!product.id" new-forms>

					<form-title title="Product Availability"/>

					<form-divider inner/>

					<v-row>
						<v-col cols="12">

							<!-- Added automatically -->
							<v-switch
									v-model="customFields.bw_add_to_future_passes.value"
									class="mb-4"
									dense
									false-value="No"
									hide-details="auto"
									inset
									label="Yes, include this product in future passes automatically."
									true-value="Yes"
							></v-switch>
							<!-- End: Added automatically -->

						</v-col>
					</v-row>

				</ui-box>

			</v-form>

			<mobile-pass-preview-modal
					v-if="preview.url"
					:preview-url="preview.url"
					@accept-preview="acceptPreview"
					@reject-preview="rejectPreview"
			></mobile-pass-preview-modal>

			<save-form
					:refs="$refs"
					:is-saving="isSaving"
					new-forms
					save-text="Save & Preview"
					@cancel-action="cancelChanges"
					@save-action="saveChanges"
			></save-form>

		</v-col>
	</v-row>
</template>

<script>
import {couponHelpers, productHelpers} from '../helpers';
import {templateHelpers} from "../../../../templateHelpers";
import FormDivider from "../../../../UI/Form/FormDivider.vue";
import VenueSelect from '../Shared/VenueSelect.vue';
import BlackoutDates from "@app/Shared/Components/BlackoutDates.vue";
import ButtonLanguage from "../Shared/ButtonLanguage.vue";

export default {
	name: "CouponProductForm",
	components: {BlackoutDates, ButtonLanguage, FormDivider, VenueSelect },
	props: {
		// The incoming product from an edit
		product: {
			type: Object,
			default: () => ({})
		},
		showSections: {
			type: Array,
			default: () => []
		},
		template: {
			type: Object,
			default: () => ({})
		},
		venue: {
			type: Object,
			default: () => ({})
		},
		venues: {
			type: Array,
			default: () => []
		},
		partner: {
			type: Object | null,
			default: null
		},
		returnUrl: {
			type: String,
			default: ''
		},
		origin: {
			type: String,
			default: ''
		},
		timezone: {
			type: String,
			default: ''
		}
	},
	data() {
		return {
			coupon: {
				...{
					// any default values here
					max_customer_uses: 0,
					max_overall_uses: 0

					// spread the product from props here to fill in the fields
					// ...this.product // obviously will need to be previously formatted
				},
				...productHelpers.productFormat(this.product)
			},
			customFields: {
				...productHelpers.customFields(this.origin, 'Yes')
			},
			formIsValid: {
				merchant: false,
				couponType: false,
				savings: false,
				details: false,
				repeat: false,
				redeem: false,
				availability: false
			},
			isSaving: false,
			phoneIsValid: true,
			preview: {
				product: {},
				url: undefined
			},
			selectedCouponTemplate: productHelpers.getTemplate(this.product, this.template, this.template.childTemplates.map(t => t.id)),
			showCouponWizard: {
				savingsInfo: false
			},
			validProductName: false
		}
	},
	mounted() {
		// this.rules = couponHelpers.getValidationRules(this.$data);
		templateHelpers.loadCustomFields(this.coupon, this.customFields);
		if(!this.coupon.tickets) this.$set(this.coupon, 'tickets', []);
		this.validProductName = productHelpers.checkGeneratedName(this.$set, this.selectedCouponTemplate, this.coupon, this.couponName, 'coupon');
	},
	computed: {
		couponName() {
			return productHelpers.getGeneratedName(this.selectedCouponTemplate, this.coupon);
		},
		hasDiscount() {
			return this.isAmountOff || this.isPercentOff;
		},
		isAmountOff() {
			return this.isCouponType('bw_dollar_off');
		},
		isBasicCoupon() {
			return this.isCouponType('bw_basic_coupon');
		},
		isBogoCoupon() {
			return this.isCouponType('bw_buy_one_get_one_free')
					|| this.isCouponType('bw_buy_one_get_one_50_off');
		},
		isDiscountCoupon() {
			return this.isCouponType('bw_dollar_off')
					|| this.isCouponType('bw_percent_off');
		},
		isPercentOff() {
			return this.isCouponType('bw_percent_off');
		},
		localVenue() {
			return this.venues.find( v => this.coupon.venue_id === v.id) || this.venue;
		},
		rules() {
			return couponHelpers.getValidationRules(this.$data);
		},
		templates() {
			// move this to the helpers
			const { children, childTemplates } = this.template;
			const childTemplatesArray = Object.values(childTemplates);
			const hasLimitedTemplates = childTemplatesArray.length < children.length;
			return hasLimitedTemplates ? childTemplatesArray : childTemplatesArray;
		}
	},
	methods: {
		acceptPreview() {
			productHelpers.acceptPreview(this.coupon, 'Coupon', this.preview, this.getReturnUrl());
		},
		cancelChanges() {
			window.location = this.getReturnUrl();
		},
		couponPropExists(obj, prop) {
			return _.has(obj, prop);
		},
		getEditUrl() {
			return this.partner
					? `/partner-portal/${this.partner.id}/products/edit-product/${this.coupon.id}`
					: `/merchant-portal/${this.localVenue.id}/products/main/edit-product/${this.coupon.id}`;
		},
		getReturnUrl() {
			return productHelpers.getReturnUrl(this.returnUrl, this.partner, this.localVenue);
		},
		isCouponType(type) {
			return this.selectedCouponTemplate?.code === type;
		},
		rejectPreview() {
			this.isSaving = false;
			this.preview.url = undefined;

			// Redirect to the edit page? Maybe not for now...
			// const currentPath = new URL(window.location.href).pathname;
			// const targetPath = new URL(`https://a.b.c${this.getEditUrl()}`).pathname;
			//
			// if(currentPath !== targetPath)
			// 	window.location.href = this.getEditUrl();
		},
		saveChanges() {
			this.coupon.name = this.couponName;
			this.coupon.customFields = Object.values(this.customFields);

			if(this.localVenue.partner_id === this.partner?.id) {
				const { pin_number, id } = this.localVenue;
				this.coupon.venue = { id, pin_number };
			}

			productHelpers.saveChanges(this.isSaving, this.partner, this.localVenue, this.coupon, this.preview);
		},
		shouldShowSection(section) {
			return couponHelpers.shouldShowSection(this.showSections, section) && this.coupon.redemption_settings;
		}
	},
	watch: {
		'coupon.settings.coupon_text': function (newVal, oldVal) {
			// Remove newline characters from the input
			this.coupon.settings.coupon_text = newVal.replace(/\s+/g, " ").trim();
		},
		selectedCouponTemplate: {
			handler(newValue) {
				this.coupon.template_id = newValue?.id || this.product.template_id;
			}
		}
	}
};
</script>

<style lang="scss">
@use 'sass:color';

.wiz-btn-icon {
	background-color: var(--v-info-darken1) !important;
	border-radius: 50% !important;
	color: white !important;
	font-size: 28px !important;
	margin: 0 0.75rem 0 -1.5rem !important;
	padding: 0.75rem !important;
}
</style>

<!-- SLIDER EXAMPLE
	<v-slider
			v-model="coupon.percent_off"
			v-if="isCouponType('bw_percent_off') && 3 === 2"
			hint="Minimum of 10% and increments of 5%"
			label="What Percentage Off?"
			min="10"
			persistent-hint
			step="5"
			thumb-label
			ticks
	>
		<template v-slot:append>
			<v-btn
					class="my-0"
					color="white"
					depressed
					icon
					:ripple="{
							class: 'secondary--text'
						}"
					small
			>
				<v-icon color="secondary">mdi-pencil-outline</v-icon>
			</v-btn>
		</template>
	</v-slider>
	END SLIDER EXAMPLE -->
